import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { StructuredData } from '../seo/structured-data';

interface BreadcrumbItem {
  label: string;
  path: string;
}

const routeLabels: Record<string, string> = {
  '/': 'Home',
  '/home': 'Home',
  '/services': 'Services',
  '/services/cctv-systems': 'CCTV Systems',
  '/services/access-control': 'Access Control',
  '/services/burglar-alarms': 'Burglar Alarms',
  '/services/24-7-monitoring': '24/7 Monitoring',
  '/services/errcs': 'ERRCS',
  '/services/fire-alarm': 'Fire Alarm Systems',
  '/services/consulting': 'Security Consulting',
  '/services/maintenance': 'Maintenance',
  '/services/support': 'Support',
  '/contact': 'Contact',
  '/about': 'About Us',
  '/support': 'Support',
  '/shop': 'Shop',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms of Service',
  '/quotes/services/cctv-systems': 'CCTV Quote',
  '/quotes/services/access-control': 'Access Control Quote',
  '/quotes/services/burglar-alarms': 'Burglar Alarm Quote',
  '/quotes/services/24-7-monitoring': 'Monitoring Quote',
  '/quotes/services/errcs': 'ERRCS Quote',
  '/quotes/services/fire-alarm': 'Fire Alarm Quote',
  '/quotes/success': 'Quote Submitted',
};

export function Breadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Don't show breadcrumbs on landing page or home page
  if (location.pathname === '/' || location.pathname === '/home') {
    return null;
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/' },
  ];

  // If we're already on home, don't add it again
  if (location.pathname === '/home') {
    // Just return Home breadcrumb, no need to process further
  } else {
    let currentPath = '';
    pathnames.forEach((pathname) => {
      currentPath += `/${pathname}`;
      // Skip if this is the home path (already added)
      if (currentPath === '/home') return;
      const label = routeLabels[currentPath] || pathname.charAt(0).toUpperCase() + pathname.slice(1).replace(/-/g, ' ');
      breadcrumbs.push({ label, path: currentPath });
    });
  }

  // Remove last item if it's the current page (we'll show it as text, not link)
  const isLast = (index: number) => index === breadcrumbs.length - 1;

  // Generate structured data for breadcrumbs
  const breadcrumbItems = breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.label,
    item: typeof window !== 'undefined' ? `${window.location.origin}${crumb.path}` : crumb.path,
  }));

  return (
    <>
      <StructuredData type="breadcrumb" data={{ items: breadcrumbItems }} />
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <li key={crumb.path} className="flex items-center">
                {index > 0 && <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />}
                {isLast(index) ? (
                  <span className="text-gray-900 font-medium" aria-current="page">
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="text-gray-600 hover:text-[#e69138] transition-colors flex items-center gap-1"
                  >
                    {index === 0 && <Home className="h-4 w-4" />}
                    {crumb.label}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </div>
      </nav>
    </>
  );
}
